import {
  AppError,
  BlockchainError,
  CommonErrorsKey,
  type SupportedChainId,
  contractBook,
  isSupportedChainId,
} from '@eulerxyz/core-sdk'
import { type BatchResultStatus, type ContractFunctionResult, createContractError } from '@eulerxyz/evc-sdk'
import type { ChainIdParameter } from '@wagmi/core/internal'
import type { Address } from 'abitype'
import type { BaseErrorType, Evaluate } from 'viem'
import { type ContractFunctionExecutionError, getAddress, zeroAddress } from 'viem'
import type { GetConnectorClientErrorType, ReadContractsErrorType } from 'wagmi/actions'
import type { ErrorType, ExtendedConfig } from '../types'

export type GetPerspectiveParameters<config extends ExtendedConfig = ExtendedConfig> = Evaluate<
  ChainIdParameter<config> & {
    address?: Address
    evcAddress?: Address
    id?: string
  }
>

export type GetPerspectiveReturnType = {
  name: BatchResultStatus<ContractFunctionResult<typeof contractBook.basePerspective.abi, 'name'>>['result']
  vaults: BatchResultStatus<ContractFunctionResult<typeof contractBook.basePerspective.abi, 'verifiedArray'>>['result']
  address: Address
  id?: string
}

export type GetPerspectiveErrorType =
  // getConnectorClient()
  | GetConnectorClientErrorType
  // base
  | BaseErrorType
  | ErrorType
  // viem
  | ReadContractsErrorType
  | ContractFunctionExecutionError
  // Custom
  | AppError
  | BlockchainError

export const TEMP_EDGE_VAULTS = new Set([getAddress('0x8B3779350Ac93eab1bEa44F96167580C1Ae6e846')])

export async function getPerspective<config extends ExtendedConfig>(
  config: config,
  parameters: GetPerspectiveParameters,
): Promise<GetPerspectiveReturnType> {
  if (!config.getEvcClient)
    throw new AppError(
      CommonErrorsKey.EVCClientNotFound,
      "Evc client isn't configured make sure you are using the `extendConfig` function with wagmi config",
    )
  if (!parameters.address) throw new AppError(CommonErrorsKey.ParamRequired, 'Address is required')

  const { address, chainId = 1, evcAddress: _evcAddress } = parameters
  if (!isSupportedChainId(chainId)) throw new AppError(CommonErrorsKey.ChainNotSupported, 'Invalid chainId')

  const evcAddress = _evcAddress ?? contractBook.evc.address[chainId as SupportedChainId]

  const evc = await config.getEvcClient({
    chainId,
    address: evcAddress,
    account: zeroAddress,
  })
  if (address === zeroAddress) {
    // We ignore the zero Perspective
    console.warn('[WARNING][getPerspective] Perspective is zero address : ', parameters)
    return {
      name: 'Unknown Perspective',
      vaults: [],
      address,
      id: parameters.id,
    }
  }

  const isEdgePerspective = address === contractBook.edgePerspective.address[chainId][0]

  const promise = Promise.all([
    evc.add({
      abi: contractBook.basePerspective.abi,
      address,
      functionName: 'name',
      onBehalfOfAccount: zeroAddress,
      value: 0n,
    }),
    evc.add({
      abi: contractBook.basePerspective.abi,
      address,
      functionName: 'verifiedArray',
      onBehalfOfAccount: zeroAddress,
      value: 0n,
    }),
    ...(isEdgePerspective
      ? [
          evc.add({
            abi: contractBook.basePerspective.abi,
            // @ts-ignore - Address might be undefined
            address: contractBook.ungoverned0xPerspective.address[chainId][0],
            functionName: 'verifiedArray',
            onBehalfOfAccount: zeroAddress,
            value: 0n,
          }),
        ]
      : []),
  ])

  return promise.then(([nameRes, verifiedRes, ungoverned0xVerifiedRes]) => {
    if (!nameRes || !verifiedRes) {
      throw new AppError(CommonErrorsKey.ParamRequired, 'Perspective not found')
    }
    if (nameRes.status === 'failure') {
      throw new BlockchainError(
        CommonErrorsKey.ErrorOnGetData,
        'Perspective name Error',
        createContractError({
          abi: contractBook.basePerspective.abi,
          address: address,
          functionName: 'name',
          sender: evc.client.account?.address,
          data: nameRes.data,
        }),
      )
    }

    if (verifiedRes.status === 'failure') {
      throw new BlockchainError(
        CommonErrorsKey.ErrorOnGetData,
        'Perspective Verified Result Error',
        createContractError({
          abi: contractBook.basePerspective.abi,
          address: address,
          functionName: 'verifiedArray',
          sender: evc.client.account?.address,
          data: verifiedRes.data,
        }),
      )
    }

    if (ungoverned0xVerifiedRes?.status === 'failure') {
      throw new BlockchainError(
        CommonErrorsKey.ErrorOnGetData,
        'Perspective Verified Result Error',
        createContractError({
          abi: contractBook.basePerspective.abi,
          address: address,
          functionName: 'verifiedArray',
          sender: evc.client.account?.address,
          data: ungoverned0xVerifiedRes.data,
        }),
      )
    }

    if (ungoverned0xVerifiedRes?.result && isEdgePerspective) {
      const ungoverned0xVaults = ungoverned0xVerifiedRes.result
      const edgeVaults = verifiedRes.result.filter((vault) => {
        const vaultAddress = getAddress(vault)
        return ungoverned0xVaults.includes(vaultAddress) || TEMP_EDGE_VAULTS.has(vaultAddress)
      })
      return {
        name: nameRes.result,
        vaults: edgeVaults,
        address,
        id: parameters.id,
      }
    }

    return {
      name: nameRes.result,
      vaults: verifiedRes.result,
      address,
      id: parameters.id,
    }
  })
}
